/* --------------------------------------------------------------------------
    PREMIUM AGENCY CONTACT & LOADING (SEO OPTIMIZED)
    Minimal Loader | Swiss Layout | Web3Forms Integrated
--------------------------------------------------------------------------- */

import React, { useEffect, useState } from "react";
import { 
  BrowserRouter as Router, 
  Routes, 
  Route, 
  useNavigate 
} from "react-router-dom";
// Removed react-helmet import as it caused a compilation error
import { motion, AnimatePresence } from "framer-motion";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Instagram, 
  Youtube, 
  ArrowUpRight, 
  CheckCircle2,
  Loader2,
  MessageCircle // Added for WhatsApp
} from "lucide-react";

// !!! YOUR KEY !!!
const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;

/* --------------------------------------------------------------------------
    THEME CONSTANTS
--------------------------------------------------------------------------- */
const THEME = {
  bg: "bg-[#F3F2ED]",
  bgDark: "bg-[#0F0F0F]",
  textMain: "text-[#0F0F0F]",
  border: "border-[#0F0F0F]/10",
};

/* --------------------------------------------------------------------------
    COMPONENTS
--------------------------------------------------------------------------- */

const AgencyButton = ({ 
  onClick, 
  children, 
  className = "", 
  disabled = false,
  type = "button"
}) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`
      group relative flex items-center justify-center gap-3 px-8 py-4 
      bg-[#0F0F0F] text-[#F3F2ED] rounded-full overflow-hidden 
      transition-all duration-300 shadow-xl shadow-black/5
      disabled:opacity-70 disabled:cursor-not-allowed
      hover:scale-[1.02] active:scale-[0.98]
      ${className}
    `}
  >
    <span className="relative z-10 font-medium tracking-wide text-sm uppercase">
      {children}
    </span>
    {!disabled && (
      <ArrowUpRight 
        size={18} 
        className="relative z-10 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" 
      />
    )}
  </button>
);

const InputField = ({ 
  label, 
  name, 
  type = "text", 
  placeholder, 
  required,
  value, 
  onChange, 
  onBlur, 
  error, 
  touched 
}) => (
  <div className="space-y-2 group">
    <label 
      htmlFor={name} // SEO: Link label to input
      className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0F0F0F]/40 transition-colors group-focus-within:text-[#0F0F0F]"
    >
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <div className="relative">
      {type === "textarea" ? (
        <textarea
          id={name}
          name={name}
          rows="4"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          aria-invalid={touched && error ? "true" : "false"} // SEO: Accessibility
          aria-describedby={touched && error ? `${name}-error` : undefined}
          className={`
            w-full bg-white px-5 py-4 text-[#0F0F0F] placeholder-[#0F0F0F]/20
            text-sm font-medium rounded-xl outline-none transition-all resize-none border
            ${touched && error 
              ? "border-red-200 bg-red-50/10 focus:border-red-500" 
              : "border-[#0F0F0F]/5 focus:border-[#0F0F0F] focus:ring-1 focus:ring-[#0F0F0F]"
            }
          `}
        />
      ) : (
        <input
          id={name}
          type={type}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          aria-invalid={touched && error ? "true" : "false"} // SEO: Accessibility
          aria-describedby={touched && error ? `${name}-error` : undefined}
          className={`
            w-full bg-white px-5 py-4 text-[#0F0F0F] placeholder-[#0F0F0F]/20
            text-sm font-medium rounded-xl outline-none transition-all border
            ${touched && error 
              ? "border-red-200 bg-red-50/10 focus:border-red-500" 
              : "border-[#0F0F0F]/5 focus:border-[#0F0F0F] focus:ring-1 focus:ring-[#0F0F0F]"
            }
          `}
        />
      )}
    </div>
    {touched && error && (
      <motion.p 
        id={`${name}-error`} // SEO: Link error to input
        initial={{ opacity: 0, y: -5 }} 
        animate={{ opacity: 1, y: 0 }}
        className="text-xs text-red-500 font-medium tracking-wide"
        role="alert"
      >
        {error}
      </motion.p>
    )}
  </div>
);

/* --------------------------------------------------------------------------
    LOADING PAGE (MINIMAL)
--------------------------------------------------------------------------- */
const LoadingPage = ({ message, redirectTo }) => {
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => navigate(redirectTo || "/contact-us"), 600);
          return 100;
        }
        const increment = Math.random() * 8 + 2; 
        return Math.min(prev + increment, 100);
      });
    }, 40);

    return () => clearInterval(interval);
  }, [navigate, redirectTo]);

  return (
    <div 
        className={`min-h-screen ${THEME.bg} flex flex-col items-center justify-center font-sans`}
        role="status" // SEO: Tells screen readers this is a status update
        aria-live="polite"
    >
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center gap-6"
      >
        {/* Minimal Counter */}
        <div className="relative">
          <span className="text-4xl md:text-5xl font-light tracking-tighter tabular-nums text-[#0F0F0F]">
            {Math.floor(progress)}%
          </span>
        </div>

        {/* Minimal Thin Line */}
        <div className="w-24 h-[1px] bg-[#0F0F0F]/10 overflow-hidden relative">
          <motion.div 
            className="absolute inset-y-0 left-0 bg-[#0F0F0F]"
            style={{ width: `${progress}%` }}
            transition={{ ease: "linear" }}
          />
        </div>

        {/* Minimal Text */}
        <p className="text-[10px] uppercase tracking-[0.25em] font-medium text-[#0F0F0F]/40 animate-pulse">
          {message || "Loading"}
        </p>

      </motion.div>
    </div>
  );
};

/* --------------------------------------------------------------------------
    CONTACT US PAGE
--------------------------------------------------------------------------- */
const ContactUs = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  // Native SEO Title
  useEffect(() => {
    document.title = "Contact Us | Let's Build It | FrameLync Agency";
  }, []);

  const validateForm = () => {
    let newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email address";
    }
    if (formData.phone && !/^\d{10}$/.test(formData.phone.replace(/[^0-9]/g, ''))) {
      newErrors.phone = "Invalid phone number";
    }
    if (!formData.message.trim()) newErrors.message = "Message cannot be empty";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleBlur = (e) => setTouched({ ...touched, [e.target.name]: true });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const allTouched = {};
    Object.keys(formData).forEach((key) => (allTouched[key] = true));
    setTouched(allTouched);

    if (validateForm()) {
      setIsSubmitting(true);
      setSubmitMessage("");

      try {
        const response = await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            access_key: WEB3FORMS_ACCESS_KEY,
            ...formData,
            subject: `New Inquiry from ${formData.name}`,
          }),
        });

        const result = await response.json();
        if (result.success) {
            setSuccess(true);
            setFormData({ name: "", email: "", phone: "", message: "" });
            setTouched({});
            setTimeout(() => setSuccess(false), 5000);
        } else {
            setSubmitMessage(result.message || "Error submitting form.");
        }
      } catch (error) {
        setSubmitMessage("Failed to send. Please check your connection.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // SEO: Structured data for the contact page.
  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact FrameLync",
    "description": "Get in touch with FrameLync for branding, motion graphics, and digital experiences.",
    "url": "https://www.framelync.com/contact-us",
    "mainEntity": {
        "@type": "Organization",
        "name": "FrameLync",
        "telephone": "+91 7619160823",
        "email": "letscreate@framelync.com",
        "address": {
            "@type": "PostalAddress",
            "addressLocality": "Mysore",
            "addressRegion": "Karnataka",
            "addressCountry": "IN"
        }
    }
  };

  return (
    <>
    <script type="application/ld+json">{JSON.stringify(contactSchema)}</script>
    
    <div className={`min-h-screen ${THEME.bg} font-sans selection:bg-black selection:text-white pt-24 md:pt-0`}>
      
      <div className="flex flex-col lg:flex-row min-h-screen">
        
        {/* LEFT PANEL: Info (Dark) */}
        <div className="w-full lg:w-[45%] bg-[#0F0F0F] text-[#F3F2ED] p-4 md:p-8 lg:p-16 flex flex-col justify-between relative overflow-hidden">
          
          <div className="relative z-10 mt-4 lg:mt-12">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] opacity-50 mb-4">
              Contact
            </h4>
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-semibold tracking-tighter leading-[0.95] mb-4">
              Let's build <br />
            </h1>
            <p className="text-lg text-white/60 font-light max-w-md leading-relaxed">
              Separate your brand from the noise. Connect with us.
            </p>
          </div>

          <div className="relative z-10 space-y-12 mt-16 lg:mt-0">
             <div className="space-y-6">
                <ContactItem icon={Phone} label="Phone" value="+91 7619160823" href="tel:+917619160823" />
                <ContactItem icon={Mail} label="Email" value="letscreate@framelync.com" href="mailto:letscreate@framelync.com" />
                <ContactItem icon={MapPin} label="Studio" value="Mysore, Karnataka, India" />
             </div>

             <div className="flex gap-4 pt-8 border-t border-white/10">
                <SocialLink href="https://instagram.com/framelyncmedia" icon={Instagram} label="Instagram" />
                <SocialLink href="https://youtube.com/@framelyncmedia" icon={Youtube} label="YouTube" />
                <SocialLink href="https://wa.me/919035250159" icon={MessageCircle} label="WhatsApp" />
             </div>
          </div>

          <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] mix-blend-overlay pointer-events-none" />
        </div>

        {/* RIGHT PANEL: Form (Light) */}
        <div className="w-full lg:w-[55%] bg-[#F3F2ED] p-8 md:p-16 lg:p-24 flex items-center">
           <div className="w-full max-w-lg mx-auto">
             <div className="mb-12">
               <h3 className="text-3xl font-bold tracking-tight text-[#0F0F0F] mb-2">Send a message</h3>
               <p className="text-[#0F0F0F]/50">We usually respond within 24 hours.</p>
             </div>

             <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                   <InputField 
                     label="Name" name="name" placeholder="John Doe" required 
                     value={formData.name} onChange={handleChange} onBlur={handleBlur} 
                     error={errors.name} touched={touched.name}
                   />
                   <InputField 
                     label="Email" name="email" type="email" placeholder="john@example.com" required 
                     value={formData.email} onChange={handleChange} onBlur={handleBlur} 
                     error={errors.email} touched={touched.email}
                   />
                </div>
                
                <InputField 
                   label="Phone" name="phone" type="tel" placeholder="Optional" 
                   value={formData.phone} onChange={handleChange} onBlur={handleBlur} 
                   error={errors.phone} touched={touched.phone}
                />
                
                <InputField 
                   label="Message" name="message" type="textarea" placeholder="Tell us about your project..." required 
                   value={formData.message} onChange={handleChange} onBlur={handleBlur} 
                   error={errors.message} touched={touched.message}
                />

                {submitMessage && (
                   <p role="alert" className="text-red-500 text-sm font-medium bg-red-50 p-3 rounded-lg border border-red-100">{submitMessage}</p>
                )}

                <div className="pt-4">
                  <AgencyButton type="submit" disabled={isSubmitting} className="w-full md:w-auto">
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="animate-spin" size={18} /> Sending...
                      </span>
                    ) : "Send Message"}
                  </AgencyButton>
                </div>
             </form>
           </div>
        </div>

      </div>

      <AnimatePresence>
        {success && (
          <motion.div
            role="alert"
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            className="fixed bottom-8 right-8 z-50 bg-[#0F0F0F] text-[#F3F2ED] shadow-2xl rounded-2xl p-6 flex items-center gap-4 pr-10 border border-white/10"
          >
            <div className="bg-green-500/20 text-green-400 p-2 rounded-full">
              <CheckCircle2 size={24} />
            </div>
            <div>
              <h4 className="font-bold text-sm tracking-wide">Message Sent</h4>
              <p className="text-white/50 text-xs mt-1">We'll be in touch shortly.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
    </>
  );
};

// --- Helper Subcomponents ---
const ContactItem = ({ icon: Icon, label, value, href }) => {
  const Content = () => (
    <div className="flex items-center gap-6 group">
       <div className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center group-hover:bg-white group-hover:text-black transition-all duration-300">
          <Icon size={20} />
       </div>
       <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/40 mb-1">{label}</p>
          <p className="text-lg font-medium">{value}</p>
       </div>
    </div>
  );

  return href ? (
    <a href={href} className="block hover:opacity-80 transition-opacity" aria-label={`Contact us via ${label}`}>
      <Content />
    </a>
  ) : (
    <Content />
  );
};

const SocialLink = ({ href, icon: Icon, label }) => (
  <a 
    href={href} 
    target="_blank" 
    rel="noreferrer"
    aria-label={label} // SEO: Explicit label for icon-only links
    className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center hover:bg-white hover:text-black transition-all duration-300"
  >
    <Icon size={20} />
  </a>
);

export default function App() {
    return (
        <Router>
            <Routes>
                <Route path="/" element={<LoadingPage message="Initializing..." redirectTo="/contact-us" />} />
                <Route path="/contact-us" element={<ContactUs />} />
            </Routes>
        </Router>
    );
}
export { LoadingPage, ContactUs };